package com.evoapps.evoshift.ui.tips

import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.widgets.OnDialog
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.VolunteerActivism
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.TipPeriod
import com.evoapps.evoshift.domain.model.TipSplit
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.viewmodels.TipEditorState
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.tips_amount
import evoshift.composeapp.generated.resources.tips_amount_invalid
import evoshift.composeapp.generated.resources.tips_delete
import evoshift.composeapp.generated.resources.tips_edit_title
import evoshift.composeapp.generated.resources.tips_next_period
import evoshift.composeapp.generated.resources.tips_period_day
import evoshift.composeapp.generated.resources.tips_period_week
import evoshift.composeapp.generated.resources.tips_preview_enter_amount
import evoshift.composeapp.generated.resources.tips_preview_title
import evoshift.composeapp.generated.resources.tips_previous_period
import evoshift.composeapp.generated.resources.tips_replaces
import evoshift.composeapp.generated.resources.tips_waiting_for_hours
import evoshift.composeapp.generated.resources.tips_weight_invalid
import evoshift.composeapp.generated.resources.tips_weights_hint
import evoshift.composeapp.generated.resources.tips_weights_title
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/**
 * SH-124: entering or changing a tip pool: a day or a week (with arrows), the amount, optional
 * weights per role of the period's hours, and the split as it would be. "Save" (the server splits
 * it again), "Remove pool" for a stored one.
 */
@Composable
fun TipPoolEditor(
    editor: TipEditorState,
    isSaving: Boolean,
    onPeriod: (TipPeriod) -> Unit,
    onMove: (Int) -> Unit,
    onAmount: (String) -> Unit,
    onWeight: (String, String) -> Unit,
    onSave: () -> Unit,
    onDelete: (() -> Unit)?,
    onDismiss: () -> Unit
) {
    val preview = editor.preview
    val start = preview?.startDate ?: editor.date.toString()
    val end = preview?.endDate ?: TipSplit.endOf(editor.period, LocalDate.parse(start)).toString()
    val on = OnDialog
    EvoDialog(
        title = stringResource(Res.string.tips_edit_title),
        subtitle = periodLabel(editor.period, start, end),
        icon = Icons.Default.VolunteerActivism,
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.save), onSave, loading = isSaving),
        neutral = onDelete?.let { DialogAction(stringResource(Res.string.tips_delete), it, destructive = true) }
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            FilterChip(selected = editor.period == TipPeriod.DAY, onClick = { onPeriod(TipPeriod.DAY) }, label = { Text(stringResource(Res.string.tips_period_day)) })
            FilterChip(selected = editor.period == TipPeriod.WEEK, onClick = { onPeriod(TipPeriod.WEEK) }, label = { Text(stringResource(Res.string.tips_period_week)) })
        }
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            IconButtonWithTooltip(onClick = { onMove(-1) }, tooltip = stringResource(Res.string.tips_previous_period)) {
                Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = stringResource(Res.string.tips_previous_period), tint = on.onSurface)
            }
            Text(periodLabel(editor.period, start, end), modifier = Modifier.weight(1f), textAlign = TextAlign.Center, color = on.onSurface, fontWeight = FontWeight.Bold)
            IconButtonWithTooltip(onClick = { onMove(1) }, tooltip = stringResource(Res.string.tips_next_period)) {
                Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = stringResource(Res.string.tips_next_period), tint = on.onSurface)
            }
        }
        if (editor.existing != null) {
            Text(stringResource(Res.string.tips_replaces), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
        }
        EvoTextField(
            value = editor.amountText,
            onValueChange = onAmount,
            label = stringResource(Res.string.tips_amount),
            error = stringResource(Res.string.tips_amount_invalid).takeIf { editor.tried && editor.amountMinor == null },
            keyboardType = KeyboardType.Decimal
        )
        if (editor.weights.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(stringResource(Res.string.tips_weights_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = LightGold)
                Text(stringResource(Res.string.tips_weights_hint), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
                editor.weights.entries.sortedBy { it.key }.forEach { (role, text) ->
                    EvoTextField(
                        value = text,
                        onValueChange = { onWeight(role, it) },
                        label = role,
                        placeholder = TipSplit.DEFAULT_WEIGHT.toString(),
                        error = stringResource(Res.string.tips_weight_invalid, TipSplit.MAX_WEIGHT).takeIf { editor.weightInvalid(role) },
                        keyboardType = KeyboardType.Number
                    )
                }
            }
        }
        Text(stringResource(Res.string.tips_preview_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = LightGold)
        when {
            preview == null -> Unit
            editor.amountMinor == null -> Text(stringResource(Res.string.tips_preview_enter_amount), color = on.onSurfaceVariant)
            preview.shares.isEmpty() -> Text(stringResource(Res.string.tips_waiting_for_hours), color = on.onSurfaceVariant)
            else -> ShareRows(preview.shares.map { Triple(it.displayName, it.minutesWorked, it.amountMinor) }, preview.currency)
        }
    }
}

